import { useFormContext } from 'react-hook-form';

import { UserInput } from '../../../../../types/registration/userInput';
import { SoknadSteg } from '../../../utils/registerEnums';
import { Energimerkeversjon } from '../../../../../types/energimerke';

import { AboutFormData } from './types';

export const useAboutFormContext = () => {
  return useFormContext<AboutFormData>();
};

export const detaljeringsstegDefault = {
  bygningsform: false,
  energibruk: false,
  vegger: false,
  vinduer: false,
  gulv: false,
  lekkasjetall: false,
  solceller: false,
  takkonstruksjon: false,
  ytterdorer: false
};

export const fromUserInputToFormData = ({
  userInput,
  registreringId,
  soknadSteg,
  energimerkeversjon
}: {
  userInput?: UserInput;
  registreringId?: string;
  soknadSteg?: SoknadSteg;
  energimerkeversjon: Energimerkeversjon;
}) => {
  const bygningsdetaljer = userInput?.bygningsdetaljer;
  const oppvarming = userInput?.tekniskUtstyr;

  const formData: AboutFormData = {
    energimerkeversjon: energimerkeversjon,
    soknadSteg: soknadSteg,
    registreringId: registreringId,
    ventilasjon: userInput?.ventilasjon,
    detaljeringssteg: userInput?.detaljeringssteg ?? detaljeringsstegDefault,
    kommunenummer: userInput?.kommunenummerForLokaltKlima,
    oppvarming: {
      oppvarmingElektrisk: {
        kjel: {
          value: oppvarming?.oppvarmingElektrisk?.kjel,
          selected: !!oppvarming?.oppvarmingElektrisk?.kjel
        },
        takvarme: {
          value: oppvarming?.oppvarmingElektrisk?.takvarme,
          selected: !!oppvarming?.oppvarmingElektrisk?.takvarme
        },
        luftvarme: {
          value: oppvarming?.oppvarmingElektrisk?.luftvarme,
          selected: !!oppvarming?.oppvarmingElektrisk?.luftvarme
        },
        panelovn: {
          value: oppvarming?.oppvarmingElektrisk?.panelovn,
          selected: !!oppvarming?.oppvarmingElektrisk?.panelovn
        },
        gulvvarme: {
          value: oppvarming?.oppvarmingElektrisk?.gulvvarme,
          selected: !!oppvarming?.oppvarmingElektrisk?.gulvvarme
        }
      },
      oppvarmingBioenergi: {
        kjel: {
          value: oppvarming?.oppvarmingBioenergi?.kjel,
          selected: !!oppvarming?.oppvarmingBioenergi?.kjel
        },
        kaminMedVannbarenVarme: {
          value: oppvarming?.oppvarmingBioenergi?.kaminMedVannbarenVarme,
          selected: !!oppvarming?.oppvarmingBioenergi?.kaminMedVannbarenVarme
        },
        kamin: {
          value: oppvarming?.oppvarmingBioenergi?.kamin,
          selected: !!oppvarming?.oppvarmingBioenergi?.kamin
        }
      },
      oppvarmingOlje: {
        kjel: {
          value: oppvarming?.oppvarmingOlje?.kjel,
          selected: !!oppvarming?.oppvarmingOlje?.kjel
        },
        kamin: {
          value: oppvarming?.oppvarmingOlje?.kamin,
          selected: !!oppvarming?.oppvarmingOlje?.kamin
        }
      },
      oppvarmingGass: {
        kjel: {
          value: oppvarming?.oppvarmingGass?.kjel,
          selected: !!oppvarming?.oppvarmingGass?.kjel
        },
        kamin: {
          value: oppvarming?.oppvarmingGass?.kamin,
          selected: !!oppvarming?.oppvarmingGass?.kamin
        }
      },
      oppvarmingVed: oppvarming?.oppvarmingVed,
      oppvarmingFjernvarme: oppvarming?.oppvarmingFjernvarme,
      oppvarmingVarmepumpe: oppvarming?.oppvarmingVarmepumpe,
      oppvarmingSol: oppvarming?.oppvarmingSol
    },
    bygginfo: {
      year: bygningsdetaljer?.byggAr,
      type: bygningsdetaljer?.byggType,
      floors: bygningsdetaljer?.antallEtasjerUnntattKjeller,
      area: bygningsdetaljer?.totalBRA,
      heatedArea: bygningsdetaljer?.totalOppvarmetBRA,
      hasBasement: bygningsdetaljer?.harBoligenKjeller,
      basementAdjoiningRoom: bygningsdetaljer?.tilliggendeRomGulvIForsteEtasje,
      isHeatedBasement: bygningsdetaljer?.heleEllerDelerAvKjellerErOppvarmet,
      material: bygningsdetaljer?.konstruksjonsmateriale,
      horizontalPlacement: bygningsdetaljer?.plasseringAvEnhetIByggHorisontalt,
      verticalPlacement: bygningsdetaljer?.plasseringAvEnhetIByggVertikalt
    },
    oppvarmingSelection: {
      oppvarmingBioenergi:
        !!oppvarming?.oppvarmingBioenergi?.kamin ||
        !!oppvarming?.oppvarmingBioenergi?.kjel ||
        !!oppvarming?.oppvarmingBioenergi?.kaminMedVannbarenVarme,
      oppvarmingElektrisk: !!oppvarming?.oppvarmingElektrisk,
      oppvarmingFjernvarme: !!oppvarming?.oppvarmingFjernvarme,
      oppvarmingGass: !!oppvarming?.oppvarmingGass,
      oppvarmingVarmepumpe: !!oppvarming?.oppvarmingVarmepumpe,
      oppvarmingVed: !!oppvarming?.oppvarmingVed,
      oppvarmingOlje: !!oppvarming?.oppvarmingOlje,
      oppvarmingSol: !!oppvarming?.oppvarmingSol
    }
  };

  return formData;
};

export const formDataToUserInput = (data: AboutFormData) => {
  const oppvarming = data?.oppvarming;

  const bioenergiValue = {
    kjel: oppvarming?.oppvarmingBioenergi?.kjel?.value,
    kamin: oppvarming?.oppvarmingBioenergi?.kamin?.value,
    kaminMedVannbarenVarme: oppvarming?.oppvarmingBioenergi?.kaminMedVannbarenVarme?.value
  };

  const gassValue = {
    kjel: oppvarming?.oppvarmingGass?.kjel.value,
    kamin: oppvarming?.oppvarmingGass?.kamin.value
  };

  const oljeValue = {
    kjel: oppvarming?.oppvarmingOlje?.kjel?.value,
    kamin: oppvarming?.oppvarmingOlje?.kamin?.value
  };

  const elektriskValue = {
    kjel: oppvarming?.oppvarmingElektrisk?.kjel.selected ? oppvarming?.oppvarmingElektrisk?.kjel.value : undefined,
    takvarme: oppvarming?.oppvarmingElektrisk?.takvarme.selected
      ? oppvarming?.oppvarmingElektrisk?.takvarme.value
      : undefined,
    panelovn: oppvarming?.oppvarmingElektrisk?.panelovn.selected
      ? oppvarming?.oppvarmingElektrisk?.panelovn.value
      : undefined,
    luftvarme: oppvarming?.oppvarmingElektrisk?.luftvarme.selected
      ? oppvarming?.oppvarmingElektrisk?.luftvarme.value
      : undefined,
    gulvvarme: oppvarming?.oppvarmingElektrisk?.gulvvarme.selected
      ? oppvarming?.oppvarmingElektrisk?.gulvvarme.value
      : undefined
  };

  const userInput: UserInput = {
    ventilasjon: data?.ventilasjon,
    tekniskUtstyr: {
      oppvarmingElektrisk: data.oppvarmingSelection?.oppvarmingElektrisk ? elektriskValue : undefined,
      oppvarmingBioenergi: data.oppvarmingSelection?.oppvarmingBioenergi ? bioenergiValue : undefined,
      oppvarmingSol: oppvarming?.oppvarmingSol,
      oppvarmingVarmepumpe: oppvarming?.oppvarmingVarmepumpe,
      oppvarmingFjernvarme: oppvarming?.oppvarmingFjernvarme,
      oppvarmingVed: oppvarming?.oppvarmingVed,
      oppvarmingGass: data.oppvarmingSelection?.oppvarmingGass ? gassValue : undefined,
      oppvarmingOlje: data.oppvarmingSelection?.oppvarmingOlje ? oljeValue : undefined
    },
    detaljeringssteg: data?.detaljeringssteg ?? detaljeringsstegDefault,
    bygningsdetaljer: {
      etasjer: [],
      byggAr: data?.bygginfo?.year,
      byggType: data?.bygginfo?.type,
      totalBRA: data?.bygginfo?.area,
      totalOppvarmetBRA: data?.bygginfo?.heatedArea,
      tilliggendeRomGulvIForsteEtasje: data?.bygginfo?.basementAdjoiningRoom,
      plasseringAvEnhetIByggVertikalt: data?.bygginfo?.verticalPlacement,
      plasseringAvEnhetIByggHorisontalt: data?.bygginfo?.horizontalPlacement,
      konstruksjonsmateriale: data?.bygginfo?.material,
      heleEllerDelerAvKjellerErOppvarmet: data?.bygginfo?.isHeatedBasement,
      harBoligenKjeller: data?.bygginfo?.hasBasement,
      antallEtasjerUnntattKjeller: data?.bygginfo?.floors
    },
    kommunenummerForLokaltKlima: data?.kommunenummer
  };

  return userInput;
};
